/**
 * The HTTP layer every embedded dashboard shares — plain `fetch`, no axios, no react-query.
 *
 * Those are excellent libraries and the Barnyard UI uses both. They are absent here on purpose:
 * anything built on this kit is embedded in host applications whose dependency trees we do not
 * control, and every runtime dependency it carries is a version conflict a host has to resolve
 * before it can "simply plug and play". React is a peer dependency and that is the whole list.
 *
 * What is in here is exactly the part that is the same for a mission tracker and a cost report:
 * acquire a token per request, attach it, turn a non-2xx into an error that carries the server's
 * own explanation, and treat 204 as "no body" rather than as a JSON parse failure. What is *not*
 * in here is any route, any resource name, any state vocabulary — those belong to the subclass.
 */
export class DashboardApiError extends Error {
    status;
    detail;
    constructor(message, status, detail) {
        super(message);
        this.status = status;
        this.detail = detail;
        this.name = 'DashboardApiError';
    }
}
export class ApiClient {
    config;
    constructor(config) {
        this.config = config;
    }
    /**
     * Which error class this client throws. A subclass overrides it so that `instanceof
     * MissionTrackerError` and `err.name` keep meaning what the host's existing catch blocks
     * expect — the base class must not quietly rename other people's errors.
     */
    error(message, status, detail) {
        return new DashboardApiError(message, status, detail);
    }
    /**
     * Build a query string, dropping only `undefined` and `null`.
     *
     * Deliberately *not* dropping other falsy values: a helper that silently swallowed `0` or
     * `false` would turn `{ limit: 0 }` into "no limit" and `{ archived: false }` into "server
     * default", which are the opposite of what was asked. Callers that want falsy-skip semantics
     * write `limit: n || undefined` at the call site, where it is visible.
     */
    qs(params) {
        const sp = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null)
                continue;
            sp.set(key, String(value));
        }
        const s = sp.toString();
        return s ? `?${s}` : '';
    }
    async request(path, init = {}) {
        const token = await this.config.getToken();
        if (!token)
            throw this.error('Not signed in', 401);
        const doFetch = this.config.fetchImpl ?? fetch;
        const res = await doFetch(`${this.config.baseUrl}${path}`, {
            ...init,
            headers: {
                ...(init.headers ?? {}),
                Authorization: `Bearer ${token}`,
                ...(init.body ? { 'Content-Type': 'application/json' } : {}),
            },
        });
        if (!res.ok) {
            // Read the body for a reason before giving up on one. The API returns
            // `{ error, detail }` on failures that have an explanation; a bodiless 500 is exactly the
            // failure mode a dashboard should not reproduce in its own error reporting.
            let detail;
            try {
                const body = (await res.json());
                detail = body.detail ?? body.error;
            }
            catch {
                /* no body, or not JSON */
            }
            throw this.error(detail ?? `Request failed (${res.status})`, res.status, detail);
        }
        if (res.status === 204)
            return undefined;
        return (await res.json());
    }
}
//# sourceMappingURL=http.js.map